import type {
  BillingGateway,
  BillingGatewayPlatform,
  BillingInterval,
  BillingState,
  DayOfWeek,
} from '../gql/graphql'

// Re-export the generated enums so existing `from '../common/types'` imports
// keep working while the values come from a single (generated) source.
export {
  BillingGateway,
  BillingGatewayPlatform,
  BillingInterval,
  BillingState,
  DayOfWeek,
} from '../gql/graphql'
import type { AppStore } from '../exports/Orchestrator/store/orchestratorStore'
import type {
  AppshellActions,
  AppshellEventKeys,
  ModalData,
  OrganizationActionKeys,
} from './events/types'

export enum MODALS {
  channelConnectionPrompt = 'channelConnectionPrompt' /* the one used in Analyze and Engage */,
  planSelector = 'planSelector',
  startTrial = 'startTrial',
  startTrialNoClick = 'startTrialNoClick',
  featureTrial = 'featureTrial',
  success = 'success',
  trialExpired = 'trialExpired',
  subscriptionUpdate = 'subscriptionUpdate',
  connectChannelUpgrade = 'connectChannelUpgrade',
  channelConnectionSuccessModal = 'channelConnectionSuccessModal',
  channelRefreshSuccessModal = 'channelRefreshSuccessModal',
  checklistComplete = 'checklistComplete',
  instagramFacebookReAuth = 'instagramFacebookReAuth',
  instagramSelectAccountType = 'instagramSelectAccountType',
  instagramSwitchToProfessionalAccountWarning = 'instagramSwitchToProfessionalAccountWarning',
  facebookSelectAccountType = 'facebookSelectAccountType',
  featurePaywall = 'featurePaywall',
  churnSurvey = 'churnSurvey',
  confirmCancellation = 'confirmCancellation',
  trialSignupWelcomeModal = 'trialSignupWelcomeModal',
  downloadApp = 'downloadApp',
  setupNotifications = 'setupNotifications',
  /* used to inform a team member has lost access due to a downgrade in the organization */
  teamMemberDowngrade = 'teamMemberDowngrade',
  postingPlanConfiguration = 'postingPlanConfiguration',
  postingGoalConfiguration = 'postingGoalConfiguration',
  channelGroupsPrompt = 'channelGroupsPrompt',
  subscriptionUpdateModern = 'subscriptionUpdateModern',
  analyzeUpgradePath = 'analyzeUpgradePath',
  switchFromIGLoginToFBLogin = 'switchFromIGLoginToFBLogin',
  annualUpgrade = 'annualUpgrade',
  cancelFlow = 'cancelFlow',
  linkedInCapabilities = 'linkedInCapabilities',
}

// Define modal priorities with a default of 1
export const modalPriorities: Record<MODALS, number> = {
  // Set default priority for all modals
  ...Object.values(MODALS).reduce(
    (acc, modal) => {
      acc[modal] = 10
      return acc
    },
    {} as Record<MODALS, number>,
  ),

  /* Feature paywalls */
  [MODALS.featurePaywall]: 20,
  [MODALS.downloadApp]: 20,

  /* modals triggered by a user action  */
  [MODALS.planSelector]: 30,
  [MODALS.churnSurvey]: 30,
  [MODALS.setupNotifications]: 30,
  [MODALS.confirmCancellation]: 30,
  [MODALS.subscriptionUpdate]: 30,
  [MODALS.success]: 30,
  [MODALS.startTrial]: 30,
  [MODALS.featureTrial]: 30,
  [MODALS.analyzeUpgradePath]: 30,

  /* startup flows that must take precedence over a pricing trial intent */
  [MODALS.annualUpgrade]: 31,
  [MODALS.linkedInCapabilities]: 31,

  /* modals related to channel connection are top priority */
  [MODALS.connectChannelUpgrade]: 40,
  [MODALS.channelConnectionSuccessModal]: 40,
  [MODALS.channelRefreshSuccessModal]: 40,
  [MODALS.instagramFacebookReAuth]: 40,
  [MODALS.instagramSelectAccountType]: 40,
  [MODALS.instagramSwitchToProfessionalAccountWarning]: 40,
  [MODALS.switchFromIGLoginToFBLogin]: 40,
}

export type ModalProps = {
  closeAction: () => void
}

export type ModalReturn = {
  data: ModalData | null
  modal: string | null
  openModal: (modalKey: string | null, modalData?: ModalData) => void
}

export type SplitState = {
  name: string
  attributes: object
  treatment: string
  isOK: boolean
  config: any
}

export type CreditCardDetails = {
  brand: string
  expMonth: string
  expYear: string
  last4: string
}

export type PaymentDetails = {
  hasPaymentDetails?: boolean | null
  creditCard?: CreditCardDetails | null
  paymentMethod?: string | null
}

export type GatewayDetails = {
  gatewayType: string
  gatewayId?: string | null
  gatewayPlatform?: BillingGatewayPlatform | null
}

export type TrialStatus = {
  isActive: boolean
  remainingDays: number
  isAwaitingUserAction: boolean
  startDate: string
  endDate: string
}

export type Plan = {
  id: string
  name: string
}

export type Subscription = {
  quantity: number
  interval: BillingInterval
  periodEnd?: string | null
  isCanceledAtPeriodEnd: boolean
  trial?: TrialStatus | null
  plan: Plan
  isPaymentPastDue?: boolean | null
  startDate?: string | null
}

export type MPSubscription = {
  interval: string
  plan: string
  product: string
  trial?: {
    isActive: boolean
  } | null
}

export type ChannelSlotDetails = {
  flatFee: number
  currentQuantity: number
  chargableQuantity: number
  pricePerQuantity: number
  minimumQuantity: number
}

export type PricingTier = {
  flatFee: number
  unitPrice: number
  upperBound: number
}

export type ChangePlanOption = {
  planId: string
  planName?: string
  planInterval: BillingInterval
  description?: string
  isCurrentPlan?: boolean
  highlightsHeading?: string
  highlights?: Array<string | null>
  currency?: string
  basePrice?: number
  totalPrice?: number
  priceNote?: string
  absoluteSavings?: string
  summary?: {
    details: Array<string | null>
  }
  isRecommended?: boolean
  downgradedMessage?: string
  channelSlotDetails?: ChannelSlotDetails | null
  pricingTiers?: Array<PricingTier>
}

export type TaxDetails = {
  automaticTax?: string | null
}

export type Billing = {
  id: string
  canAccessAnalytics: boolean
  canAccessEngagement: boolean
  canAccessPublishing: boolean
  canStartTrial?: boolean
  paymentDetails: PaymentDetails
  subscription?: Subscription
  subscriptions?: MPSubscription[]
  gateway?: GatewayDetails
  channelSlotDetails?: ChannelSlotDetails
  pricingTiers?: Array<PricingTier>
  billingRedirectUrl?: string
  isAutomaticTaxEnabled?: boolean | null
  taxDetails?: TaxDetails | null
  taxExempt?: string | null
}

export type PostingSchedule = {
  day: DayOfWeek
  times: string[]
  paused: boolean
}

export type Channel = {
  id: string
  isLocked: boolean
  name: string
  organizationId: string
  service: string
  serviceId: string
  type?: string
  avatar?: string
  createdAt?: string
  serverUrl?: string | null
  shouldShowPinterestUpgradeBanner?: boolean
  shouldShowLinkedInUpgradeBanner?: boolean
  shouldShowTiktokUpgradeBanner?: boolean
  shouldShowIgLoginRefreshBanner?: boolean
  apiVersion?: string | null
  postingSchedule?: PostingSchedule[]
}

export type Product = {
  name: string
  userId?: string | null
}

export type CommonTrackingProperties = {
  atEventBillingState: BillingState
  atEventBillingPlan: string
  atEventBillingCycle: BillingInterval
  atEventBillingGateway: BillingGateway
  atEventIsNewBuffer: boolean
  atEventChannelQty: number
}

export type DismissedObject = {
  id: string
  expiresAt?: Date
}

export type LifetimeConnectedChannelCount = {
  service?: string | null
  serviceIds?: Array<string | null> | null
}

export type Organization = {
  /** The organization actions the current account can perform. */
  allowedActions?: string[]
  billing?: Billing
  channelRecommendations?: string[]
  channels?: Channel[]
  createdAt?: string
  featureFlips?: string[]
  id?: string
  channelLimit?: number
  isOneBufferOrganization?: boolean
  name?: string
  role?: string
  shouldDisplayInviteCTA?: boolean
  commonTrackingProperties?: CommonTrackingProperties
  dismissedObjects?: DismissedObject[]
  isBetaEnabled?: boolean
  isAlphaEnabled?: boolean
  lifetimeConnectedChannelsCount?: LifetimeConnectedChannelCount[]
  shouldShowTeamMemberOrganizationDowngradedBanner?: boolean
  ownerEmail?: string
  members?: {
    totalCount: number
  }
  settings?: {
    enforce2FA: boolean
  }
  shouldEnforce2FASetup?: boolean
}

export type Account = {
  avatar?: string
  preferences?: {
    startOfWeek?: string | null
  } | null
  channels?: Channel[]
  createdAt?: string | null
  currentOrganization?: Organization | null
  email?: string
  hasVerifiedEmail?: boolean | null
  helpScoutBeaconSignature?: string | null
  featureFlips?: string[]
  id?: string
  name?: string | null
  isImpersonation?: boolean
  organizations?: Organization[]
  products?: Product[] | null
  shouldShowEmailVerificationCommunication?: boolean | null
  splits?: SplitState[]
  dismissedBanners?: Array<string | null> | null
  hellonextSsoToken?: string | null
  tfa?: {
    type?: string | null
  } | null
}

export type WindowAppShell = {
  eventKeys: typeof AppshellEventKeys
  actions: AppshellActions
  actionKeys: typeof OrganizationActionKeys
  MODALS: typeof MODALS
  orchestratorStore: AppStore
}

export type PromiseCB = () => Promise<void>
